import React from "react";
import {
    RoadblockAwaitableEntityCategory_displayString,
    TRoadblockAwaitableEntity,
} from "../../../../../domain/codecs/form/RoadblockForm";
import { AWAIT_DAYS_MAX, AWAIT_DAYS_MIN } from "../../../../../domain/codecs/form/ChatsMessageForm";
import { Background } from "../../BuildingBlocks/Background";
import { SpacingColumn } from "../../BuildingBlocks/SpacingColumn";
import { SpacingRow } from "../../BuildingBlocks/SpacingRow";
import { CRMSpacing } from "../../../models/CRMSpacing";
import { CRMParagraph } from "../../Simple/CRMParagraph/CRMParagraph";
import { WeightBold } from "../../WeightBold/WeightBold";
import { toDateFormat1 } from "../../../../../shared/src/dateTime";
import { createStartOfBusinessDaysFromNow } from "../../../../../domain/models/ISODateTime";
import {
    flattenAwaitEntities,
    groupAwaitEntities,
    matchesAwaitQuery,
    resolveRecentEntities,
} from "./awaitEntitySearch";

const RECENT_LIMIT = 3;

type TActiveAwait = {
    waiting_on: string;
    blocked_till: string;
    is_internal: boolean;
};

type TCRMRoadblockAwaitPopoutProps = {
    entities: Array<TRoadblockAwaitableEntity>;
    activeAwait: TActiveAwait | null;
    recentEntityIds: Array<string>;
    bulkCount: number | null;
    listMaxHeightPx: number | null;
    onPick: (awaitableEntityId: string, days: number | null) => void;
    onCancel: () => void;
    onRequestClose: () => void;
};

const clampDays = (value: number): number =>
    Math.min(AWAIT_DAYS_MAX, Math.max(AWAIT_DAYS_MIN, value));

const EntityRow = (props: {
    entity: TRoadblockAwaitableEntity;
    isCursor: boolean;
    onPick: () => void;
}): JSX.Element => (
    <div
        className={
            "crm-roadblock-await-popout__entity"
            + (props.isCursor ? " crm-roadblock-await-popout__entity--cursor" : "")
            + (props.entity.is_internal ? " crm-roadblock-await-popout__entity--internal" : "")
        }
        onClick={props.onPick}
    >
        <CRMParagraph>
            <WeightBold>{props.entity.name}</WeightBold>
        </CRMParagraph>

        <div className="crm-roadblock-await-popout__entity-default">
            <CRMParagraph>{props.entity.blocked_until_days}d</CRMParagraph>
        </div>
    </div>
);

export const CRMRoadblockAwaitPopout = (props: TCRMRoadblockAwaitPopoutProps): JSX.Element => {
    const [query, setQuery] = React.useState<string>("");
    const [cursor, setCursor] = React.useState<number>(0);
    const [daysOverride, setDaysOverride] = React.useState<number | null>(null);
    const searchRef = React.useRef<HTMLInputElement>(null);

    React.useEffect(() => {
        searchRef.current?.focus();
    }, []);

    const matching = props.entities.filter((entity) => matchesAwaitQuery(entity, query));
    const groups = groupAwaitEntities(matching);
    const recents = query.trim() === ""
        ? resolveRecentEntities(matching, props.recentEntityIds, RECENT_LIMIT)
        : [];
    const flat = flattenAwaitEntities(groups, recents);

    const safeCursor = Math.min(cursor, Math.max(0, flat.length - 1));
    const cursorEntity: TRoadblockAwaitableEntity | undefined = flat[safeCursor];
    const days = daysOverride !== null
        ? daysOverride
        : (cursorEntity?.blocked_until_days ?? AWAIT_DAYS_MIN);

    const pick = (entity: TRoadblockAwaitableEntity | undefined) => {
        if (entity === undefined) {
            return;
        }
        props.onPick(entity.id, daysOverride);
    };

    const onSearchChange = (value: string) => {
        setQuery(value);
        setCursor(0);
        setDaysOverride(null);
    };

    const moveCursor = (delta: number) => {
        setCursor(Math.min(Math.max(safeCursor + delta, 0), Math.max(0, flat.length - 1)));
    };

    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "ArrowDown") {
            event.preventDefault();
            moveCursor(1);
        }
        if (event.key === "ArrowUp") {
            event.preventDefault();
            moveCursor(-1);
        }
        if (event.key === "Enter") {
            event.preventDefault();
            pick(cursorEntity);
        }
        if (event.key === "Escape") {
            event.preventDefault();
            props.onRequestClose();
        }
    };

    let paintedIndex = -1;

    return (
        <div className="crm-roadblock-await-popout">
            <Background padding={CRMSpacing.SMALL}>
                <SpacingColumn spacing={CRMSpacing.TINY}>
                    <CRMParagraph>
                        <WeightBold>
                            {props.bulkCount !== null
                                ? `Await ${props.bulkCount} roadblock${props.bulkCount === 1 ? "" : "s"}…`
                                : "Await…"
                            }
                        </WeightBold>
                    </CRMParagraph>

                    <input
                        ref={searchRef}
                        className="crm-roadblock-await-popout__search"
                        value={query}
                        placeholder="Search parties…"
                        aria-label="Search awaitable parties"
                        onChange={(e) => onSearchChange(e.target.value)}
                        onKeyDown={onKeyDown}
                    />

                    {props.activeAwait !== null && <>
                        <CRMParagraph>
                            <WeightBold>
                                {props.activeAwait.is_internal
                                    ? props.activeAwait.waiting_on
                                    : `Chase: ${props.activeAwait.waiting_on}`
                                }
                            </WeightBold>
                            {` · until ${toDateFormat1(props.activeAwait.blocked_till)}`}
                        </CRMParagraph>

                        <div
                            className="crm-roadblock-await-popout__cancel"
                            onClick={props.onCancel}
                        >
                            <CRMParagraph>
                                <WeightBold>Cancel await</WeightBold>
                            </CRMParagraph>
                        </div>
                    </>}

                    <div
                        className="crm-roadblock-await-popout__list"
                        style={props.listMaxHeightPx === null ? undefined : { maxHeight: `${props.listMaxHeightPx}px` }}
                    >
                        {recents.length > 0 &&
                            <div className="crm-roadblock-await-popout__group-label">
                                <CRMParagraph>
                                    <WeightBold>RECENT</WeightBold>
                                </CRMParagraph>
                            </div>
                        }
                        {recents.map((entity) => {
                            paintedIndex = paintedIndex + 1;
                            const index = paintedIndex;
                            return (
                                <EntityRow
                                    key={`recent-${entity.id}`}
                                    entity={entity}
                                    isCursor={index === safeCursor}
                                    onPick={() => pick(entity)}
                                />
                            );
                        })}

                        {groups.map((group) => (
                            <React.Fragment key={group.category}>
                                <div className="crm-roadblock-await-popout__group-label">
                                    <CRMParagraph>
                                        <WeightBold>
                                            {RoadblockAwaitableEntityCategory_displayString(group.category).toUpperCase()}
                                        </WeightBold>
                                    </CRMParagraph>
                                </div>
                                {group.entities.map((entity) => {
                                    paintedIndex = paintedIndex + 1;
                                    const index = paintedIndex;
                                    return (
                                        <EntityRow
                                            key={entity.id}
                                            entity={entity}
                                            isCursor={index === safeCursor}
                                            onPick={() => pick(entity)}
                                        />
                                    );
                                })}
                            </React.Fragment>
                        ))}

                        {flat.length === 0 &&
                            <CRMParagraph>No matching parties.</CRMParagraph>
                        }
                    </div>

                    {flat.length > 0 &&
                        <div className="crm-roadblock-await-popout__duration">
                            <SpacingRow spacing={CRMSpacing.TINY} alignItems="center">
                                <CRMParagraph>for</CRMParagraph>

                                <div className="crm-roadblock-await-popout__stepper">
                                    <button
                                        type="button"
                                        aria-label="Fewer business days"
                                        onClick={() => setDaysOverride(clampDays(days - 1))}
                                    >
                                        −
                                    </button>
                                    <input
                                        className="crm-roadblock-await-popout__days-input"
                                        inputMode="numeric"
                                        value={String(days)}
                                        aria-label="Business days"
                                        onChange={(e) => {
                                            const digits = e.target.value.replace(/\D/g, "");
                                            setDaysOverride(digits === "" ? null : clampDays(Number(digits)));
                                        }}
                                    />
                                    <button
                                        type="button"
                                        aria-label="More business days"
                                        onClick={() => setDaysOverride(clampDays(days + 1))}
                                    >
                                        +
                                    </button>
                                </div>

                                <CRMParagraph>business days</CRMParagraph>
                            </SpacingRow>

                            {/* Uses the same helper the server uses to set blocked_until, so the
                                preview cannot drift from the date actually written. */}
                            <CRMParagraph>
                                {"Lands on "}
                                <WeightBold>
                                    {toDateFormat1(createStartOfBusinessDaysFromNow(days))}
                                </WeightBold>
                                {daysOverride === null && cursorEntity !== undefined
                                    && ` · ${cursorEntity.name}'s default`}
                                {cursorEntity?.is_internal === true && " · shows as Parked, not a chase"}
                            </CRMParagraph>

                            <div className="crm-roadblock-await-popout__hint">
                                <CRMParagraph>↑↓ move · ↵ await · esc close</CRMParagraph>
                            </div>
                        </div>
                    }
                </SpacingColumn>
            </Background>
        </div>
    );
};
